<template>
  <div id="health-insight-detail">
    <WorkPerformed
      :show="workPerformedShow"
      :hide="reset"
      :serviceAlerts="workPerformed"
    />
    <ClearAlerts
      :show="clearAlertShow"
      :allAlerts="notifications"
      :hide="reset"
      :clearAlerts="clearAlerts"
      :status="status"
      :actionNote="actionNote"
      :actionNoteChange="actionNoteChange"
      :actionUnmuteDateChange="actionUnmuteDateChange"
      :unmuteDate="unmuteDate"
    />
    <UpdateAlertStatus
      :show="updateAlertShow"
      :status="alertStatus"
      :allAlerts="alertNotifications"
      :hide="reset"
      :updateAlertStatus="updateAlertStatus"
    />
    <div class="hid-header-container">
      <div class="hid-header">
        Health Insight
      </div>
      <div class="hid-table-header-container">
        <div class="hid-table-header">
          CATEGORY
        </div>
        <div class="hid-table-header">
          STATUS & URGENCY
        </div>
        <div class="hid-table-header two-line">
          ALERT TYPE & DESCRIPTION
        </div>
        <div class="hid-table-header">
          ZONE | VALUE | OCCURRENCES
        </div>
        <div class="hid-table-header">
          FIRST - LAST OCCURRED
        </div>
        <!--      <div class="hid-table-header">-->
        <!--        ALERT SENT ON-->
        <!--      </div>-->
        <!--      <div class="hid-table-header">-->
        <!--        ACTIONS TAKEN-->
        <!--      </div>-->
        <div class="hid-table-header">
          SENSOR COMMS STATUS
        </div>
        <div
          v-if="showPerformanceOptions"
          class="hid-table-header"
        >
          SERVICE INSIGHTS
        </div>
      </div>
    </div>
    <div class="hid-body-container">
      <div class="hid-b-header-container">
        <div class="sub-active-container">
          <div class="hid-b-header">
            Active
          </div>
          <div class="hid-b-header-count">
            {{ notifications.length }}
          </div>
        </div>
        <div class="sub-action-container">
          <div
            :class="[
              show ? 'active' : '',
              'clear-options-dropdown',
            ]"
          >
            <div
              v-if="showPerformanceOptions"
              class="action-option"
              @click="workPerformedShow = !workPerformedShow; show = false"
            >
              Service Performed
            </div>
            <div
              class="action-option"
              @click="actionDismiss('Dismissed - Invalid', 'Dismissed')"
            >
              Dismiss - Invalid
            </div>
            <div
              class="action-option"
              @click="actionDismiss('Dismissed - Known Issue', 'Dismissed')"
            >
              Dismiss - Known Issue
            </div>
            <div
              class="action-option"
              @click="actionDismiss('Resolved by Member', 'Resolved')"
            >
              Dismiss - Resolved by Member
            </div>
            <div
              class="action-option"
              @click="actionDismiss('Dismissed - Known Issue', 'Dismissed_Chronic')"
            >
              Dismiss - Known Issue AND Stop Alerts
            </div>
          </div>
        </div>
      </div>
      <div class="hid-alert-container">
        <div
          v-for="(n, idx) in notifications"
          :key="idx"
          class="hid-alert"
        >
          <div
            v-if="n.startCategory"
            :class="[
              'hid-a-category',
              n.startCategory ? 'start-category' : '',
              n.endCategory ? 'end-category' : '']"
          >
            <div class="category">
              {{ n.category }}
            </div>
          </div>
          <div
            v-else
            :class="['hid-a-category', n.endCategory ? 'end-category' : '']"
          >
            <div class="hidden-category" />
          </div>
          <div class="hid-a-item">
            <div class="highlight-status">
              <span class="status-dot" />
              <span>{{ n.status }}</span>
            </div>
            <div class="highlight-severity">
              <span :class="[checkSeverity(n.severity)]">{{ n.severity }}</span>
            </div>
          </div>
          <div class="hid-a-item two-line">
            {{ n.title }}: {{ getDescription(n) }}
          </div>
          <div class="hid-a-item">
            {{ hvac.systemName }} | {{ getUnit(n) }} | {{ n.occurrence }}
          </div>
          <div class="hid-a-item">
            {{ customDateFilter(n.firstOccurred, dateFormat) }} - {{ customDateFilter(n.lastOccurred, dateFormat) }}
          </div>
          <div
            :class="[
              hvac.deviceSensors[n.did - 1].commStatus === 'good' ? 'green-text' : '',
              hvac.deviceSensors[n.did - 1].commStatus === 'warning' ? 'yellow-text' : '',
              hvac.deviceSensors[n.did - 1].commStatus === 'low' ? 'red-text' : '',
              hvac.deviceSensors[n.did - 1].commStatus === 'n/a' ? '' : '',
              hvac.deviceSensors[n.did - 1].commStatus === '' ? 'red-text' : '',
              'hid-a-item hid-a-comm']"
          >
            DID: {{ n.did }} | {{ deviceStatus(hvac.deviceSensors[n.did - 1].commStatus) }}
            | Last Update: {{ getFormattedDate(hvac.deviceSensors[n.did - 1].lastCommStatusDate) }}
          </div>
          <div
            v-if="showPerformanceOptions"
            class="hid-a-graph"
          >
            <DeltaTGraph
              v-if="n.cycles && n.cycles.length > 0 && n.type === 'Cool Delta T'"
              :graphData="n.cycles"
              :status="n.severity"
              :type="n.type"
            />
            <SupplyAirGraph
              v-else-if="n.cycles
                && n.cycles.length > 0
                && (n.type === 'Cool Supply Air' || n.type === 'Heat Supply Air')"
              :graphData="n.cycles"
              :status="n.severity"
              :type="n.type"
            />
            <RuntimeGraph
              v-else-if="n.cycles
                && n.cycles.length > 0
                && (n.type === 'Cool Runtime' || n.type === 'Heat Runtime')"
              :graphData="n.cycles"
              :status="n.severity"
              :type="n.type"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import systemHealthMixin from '@shared/mixins/systemHealthMixin';
import unitPreferencesMixin from '@shared/mixins/unitPreferencesMixin';

import DeltaTGraph from './DeltaTGraph.vue';
import SupplyAirGraph from './SupplyAirGraph.vue';
import RuntimeGraph from './RuntimeGraph.vue';
import WorkPerformed from '../shared/WorkPerformed.vue';
import ClearAlerts from '../shared/ClearAlerts.vue';
import UpdateAlertStatus from '../shared/UpdateAlertStatus.vue';

export default {
  name: 'HealthInsightDetail',
  components: {
    UpdateAlertStatus,
    RuntimeGraph,
    SupplyAirGraph,
    DeltaTGraph,
    WorkPerformed,
    ClearAlerts,
  },
  mixins: [systemHealthMixin, unitPreferencesMixin],
  data() {
    return {
      show: false,
      workPerformedShow: false,
      clearAlertShow: false,
      updateAlertShow: false,
      alertStatus: '',
      alertNotifications: [],
      showPerformanceOptions: false,
    };
  },
  beforeMount() {
    if (this.notifications.length) {
      this.notifications.forEach((n) => {
        if (n.category === 'Performance' && n.allCycles) {
          this.showPerformanceOptions = true;
        }
      });
    }
  },
  mounted() {
    document.querySelector(':not(.clear-options-dropdown)').addEventListener('click', this.closeOnClick);
  },
  destroyed() {
    document.querySelector(':not(.clear-options-dropdown)').removeEventListener('click', this.closeOnClick);
  },
  methods: {
    actionNoteChange(note) {
      this.$store.dispatch('changeHomeValue', {
        field: 'actionNote',
        value: note,
      });
    },
    actionUnmuteDateChange(date) {
      date = moment(date).format('YYYY-MM-DD');
      this.$store.dispatch('changeHomeValue', {
        field: 'unmuteDate',
        value: date,
      });
    },
    actionDismiss(reason, status) {
      this.$store.dispatch('changeHomeValue', {
        field: 'actionDismissReason',
        value: reason,
      });
      this.$store.dispatch('changeHomeValue', {
        field: 'status',
        value: status,
      });
      this.clearAlertShow = !this.clearAlertShow;
      this.show = false;
    },
    checkSeverity(severity) {
      if (severity) {
        if (severity === 'Urgent'
          || severity.includes('Attention')
          || severity.includes('Action')) {
          return 'urgent-severity-tag';
        }
        if (severity === 'Watching') {
          return 'watching-severity-tag';
        }
      }
      return '';
    },
    handleToggle() {
      this.show = !this.show;
    },
    workPerformed({ date, technicianName, workPerformed }) {
      this.reset();
      this.$store.dispatch('workPerformedAlerts', {
        date,
        technicianName,
        workPerformed,
        alerts: this.notifications,
      });
    },
    clearAlerts(alerts) {
      this.reset();
      this.$store.dispatch('dismissAlerts', {
        alerts,
      });
    },
    updateAlertStatus(alerts, status) {
      this.reset();
      this.$store.dispatch('updateAlertsStatus', {
        alerts,
        allAlerts: this.notifications,
        status,
      });
    },
    reset() {
      this.workPerformedShow = false;
      this.clearAlertShow = false;
      this.updateAlertShow = false;
    },
    closeOnClick(e) {
      if (!e.target.closest('.clear-options-dropdown') && this.show) {
        this.show = false;
      }
    },
  },
};
</script>

<style lang="scss">

#health-insight-detail {
  display: flex;
  box-shadow: 0 1px 10px 0 rgba(9, 15, 32, 0.1);
  border-radius: 4px;
  .hid-header-container {
    display: flex;
    flex-direction: column;
    background-color: $WHITE;
    padding: 1rem 0 1rem 1rem;
    width: 250px;
    box-shadow: 1px 0 4px -2px rgba(0, 0, 0, 0.25);
    z-index: 1;
    border-top-left-radius: 4px;
    border-bottom-left-radius: 4px;
    .hid-header {
      padding: 8px;
      height: 50px;
      color: $N60;
      font-weight: 600;
      font-size: 17px;
      border-bottom: 1px solid $N10;
    }
    .hid-table-header-container {
      padding: 8px 0;
      .hid-table-header {
        display: flex;
        align-items: center;
        padding: 0 4px;
        color: $N50;
        font-weight: 700;
        font-size: 0.875rem;
        height: 38px;
        &.two-line {
          height: 65px;
          padding: 10px 4px;
          align-items: flex-start;
        }
      }
    }
  }
  .hid-body-container {
    background-color: $WHITE;
    padding: 1rem 0 0 0;
    border-top-right-radius: 4px;
    border-bottom-right-radius: 4px;
    .hid-b-header-container {
      display: flex;
      height: 50px;
      align-items: center;
      justify-content: space-between;
      padding: 4px 16px 5px 16px;
      border-bottom: 1px solid $N10;
      .sub-active-container {
        display: flex;
        .hid-b-header {
          color: $N60;
          font-weight: 600;
          font-size: 17px;
        }
        .hid-b-header-count {
          background-color: $N100;
          color: $WHITE;
          width: 23px;
          height: 23px;
          border-radius: 50%;
          margin-left: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.875rem;
          font-weight: 700;
        }
      }
      .sub-action-container {
        position: relative;
        .action-btn {
          background-color: $N100;
          color: $WHITE;
          border-radius: 5px;
          display: grid;
          grid-auto-flow: column;
          width: 80px;
          gap: 5px;
          padding: 4px 10px;
          align-items: center;
          justify-content: space-between;
        }
        .clear-options-dropdown {
          justify-content: center;
          display: grid;
          grid-auto-flow: row;
          top: 35px;
          right: -9px;
          position: absolute;
          background-color: $WHITE;
          width: 250px;
          padding: 12px 0;
          box-shadow: 0 1px 27px 12px rgba(9,15,32,0.1);
          border-radius: 6px;
          transition: transform 0.1s ease;
          transform: scaleY(0);
          transform-origin: top;
          color: $BLACK;
          font-size: 14.22px;
          line-height: 30px;
          &.active {
            transform: scaleY(1);
          }
          &:after {
            content: '';
            position: absolute;
            top: 0;
            right: 15px;
            width: 0;
            height: 0;
            border: 8px solid transparent;
            border-bottom-color: $WHITE;
            border-top: 0;
            margin-left: -8px;
            margin-top: -8px;
          }
          .action-option {
            padding: 4px 32px;
            cursor: pointer;
            &:hover {
              background-color: $NBLUE10;
            }
          }
          .disable-clear-alerts {
            pointer-events: none;
            color: $N30;
          }
        }
      }
    }
    .hid-alert-container {
      display: flex;
      width: calc(100vw - 514px);
      overflow-x: auto;
      .hid-alert {
        display: flex;
        flex-direction: column;
        min-width: 500px;
        max-width: 500px;
        padding: 8px 0;
        .hid-a-item {
          height: 38px;
          display: flex;
          align-items: center;
          padding: 0 16px;
          border-right: 1px dotted $N10;
          &:not(:last-child) {
            border-bottom: 1px dotted $N10;
          }
          &.two-line {
            height: 65px;
            padding: 10px 16px;
            align-items: flex-start;
          }
        }
        .hid-a-comm {
          text-transform: capitalize;
        }
        .hid-a-graph {
          height: 400px;
          width: 500px;
          justify-content: center;
          display: flex;
          padding: 8px 16px;
          border-right: 1px dotted $N10;
          .hid-a-no-data {
            width: 450px;
            height: 350px;
            background-color: $N10;
            border-radius: 4px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: $BLACK;
            font-size: 1.125rem;
            font-weight: 500;
          }
        }
        .highlight-severity {
          .urgent-severity-tag {
            background-color: $TOMATO25;
            padding: 2px 8px;
            border-radius: 20px;
            color: $TOMATO100;
            font-weight: 600;
            letter-spacing: .3px;
          }
          .watching-severity-tag {
            background-color: $SUNSET10;
            padding: 2px 8px;
            border-radius: 20px;
            color: $SUNSET100;
            font-weight: 600;
            letter-spacing: .3px;
          }
        }
        .highlight-status {
          background-color: $N10;
          margin-right: 12px;
          font-weight: 600;
          font-size: 13px;
          padding: 4px 12px;
          border-radius: 15px;
          text-align: center;
          display: grid;
          grid-auto-flow: column;
          align-items: center;
          justify-content: flex-start;
          gap: 10px;
          .status-dot {
            background-color: $LIGHTBLUE;
            height: 12px;
            width: 12px;
            border-radius: 50%;
            display: inline-block;
          }
        }
        .hid-a-category {
          height: 38px;
          display: flex;
          align-items: center;
          border-bottom: 1px dotted $N10;
          font-weight: 600;
          font-size: 15px;
          .category {
            background-color: $N1;
            width: 100%;
            height: 28px;
            display: flex;
            align-items: center;
            padding: 0 16px;
          }
          .hidden-category {
            background-color: $N1;
            width: 100%;
            height: 28px;
          }
        }
        .start-category {
          padding-left: 10px;
          .category {
            border-bottom-left-radius: 5px;
            border-top-left-radius: 5px;
          }
        }
        .end-category {
          padding-right: 10px;
          border-right: 1px dotted $N10;
          .category, .hidden-category {
            border-bottom-right-radius: 5px;
            border-top-right-radius: 5px;
          }
        }
      }
    }
  }
}
</style>
